import { useState } from 'react'
import type { ChunkSummary } from '@/features/document-overlay/grouping'

const CHUNK_PREVIEW_CHAR_LIMIT = 500
const CHUNK_EXPANDED_MAX_HEIGHT = 360

export function ChunkCard({ chunk }: { chunk: ChunkSummary }) {
  const [expanded, setExpanded] = useState(false)
  const canCollapse = chunk.text.length > CHUNK_PREVIEW_CHAR_LIMIT
  const displayText =
    expanded || !canCollapse ? chunk.text : `${chunk.text.slice(0, CHUNK_PREVIEW_CHAR_LIMIT)}…`

  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: '12px',
        background: 'var(--card)',
        border: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 10.5,
          color: 'var(--muted-foreground)',
          fontFamily: 'var(--font-mono)',
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        <span>#{chunk.chunkIndex ?? '?'}</span>
        {chunk.chunkType && chunk.chunkType !== 'text' && <span>· {chunk.chunkType}</span>}
        {chunk.section && (
          <span
            style={{
              textTransform: 'none',
              letterSpacing: 0,
              color: 'var(--foreground)',
              fontFamily: 'inherit',
            }}
          >
            · {chunk.section}
          </span>
        )}
        <span
          className="max-md:shrink-0 max-md:whitespace-nowrap"
          style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0 }}
        >
          {chunk.text.length.toLocaleString()} chars
        </span>
      </div>
      <div
        style={{
          fontSize: 12.5,
          lineHeight: 1.55,
          color: 'var(--foreground)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: expanded ? CHUNK_EXPANDED_MAX_HEIGHT : undefined,
          overflowY: expanded ? 'auto' : undefined,
        }}
      >
        {chunk.text ? (
          displayText
        ) : (
          <span style={{ color: 'var(--muted-foreground)' }}>(empty chunk)</span>
        )}
      </div>
      {canCollapse && (
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          // The negative margin cancels the tap-target padding, so the card keeps its spacing.
          className="m-0 p-0 max-md:-my-2 max-md:py-2"
          style={{
            alignSelf: 'flex-start',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--primary)',
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}
